import { EsgRecord } from '../../../types/syntiro_honey';

/** The organisational level a record's reporting entity resolved to. */
export type EntityLevel = 'Site' | 'Sub-division' | 'Division' | 'Company';

/** A record's resolved reporting entity: its display name and the level it attaches at. */
export interface ResolvedEntity {
  /** The deepest resolved entity's name (e.g. "Walvis Bay" or "CCS"). */
  name: string;
  /** The organisational level that name sits at. */
  level: EntityLevel;
}

/** The record fields the entity resolution reads. */
type EntityFields = Pick<
  EsgRecord,
  'siteName' | 'subdivisionName' | 'divisionName' | 'companyName' | 'companyWide'
>;

/**
 * Resolves a record's reporting entity to the DEEPEST level it attached at
 * (site → sub-division → division), or null when it resolved to none. A record may
 * legitimately attach above site level — e.g. a division total with no site — so the
 * level is surfaced to the reviewer rather than assuming every entity is a site.
 *
 * A record a reviewer deliberately attached to the whole COMPANY resolves to the
 * company itself. That is only true when the record carries the `companyWide`
 * marker: a record the pipeline simply could not place carries the same empty
 * division/sub-division/site and must keep reading as unattributed, because it
 * still needs a human. Before the marker existed the two were indistinguishable,
 * and a deliberate company-level assignment showed as a blank field
 * (Maxine 2026-08-14 ⑨).
 */
export function resolveRecordEntity(record: EntityFields): ResolvedEntity | null {
  if (record.siteName !== null) {
    return { name: record.siteName, level: 'Site' };
  }
  if (record.subdivisionName !== null) {
    return { name: record.subdivisionName, level: 'Sub-division' };
  }
  if (record.divisionName !== null) {
    return { name: record.divisionName, level: 'Division' };
  }
  const companyName = record.companyName ?? null;
  if (record.companyWide === true && companyName !== null) {
    return { name: companyName, level: 'Company' };
  }
  return null;
}
